import { captureException } from '@sentry/react';
import { classifyHttpError, isCustomError, UnknownError } from 'errors';
import includes from 'lodash/includes';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { stringify } from 'query-string';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { UAParser } from 'ua-parser-js';
import { trackEvent, uaHelper } from 'utils';

import { formatCurrentTime } from '../../utils/src/times';
import {
  buildFundamentalOneLink,
  buildFundamentalOneLinkForIos,
  buildFundamentalScheme,
  buildNewsHot,
  buildQuoteSummary,
  buildViewAssetOneLink,
  buildViewAssetScheme,
  buildViewStockOneLink,
  delayedMarketList,
  newsWebHost,
} from './constants';
import { FundamentalScoreMark } from './FundamentalScoreMark';
import { NewsIdContext } from './News';
import { AMPLITUDE_DISCLOSURE_TYPE, BannerOs, DisclosureType, NewsType } from './types';
import { request } from './utils/request';

const parser = new UAParser();
enum ScoreTypes {
  재무우량,
  재무양호,
  고평가,
  저평가,
  고성장,
  저성장,
  고수익,
  저수익,
  대형주,
  중대형,
  중형주,
  소형주,
  초소형,
}

type Change = 'UPPER_LIMIT' | 'RISE' | 'EVEN' | 'LOWER_LIMIT' | 'FALL';

const changeClass = {
  UPPER_LIMIT: 'up1',
  RISE: 'up',
  EVEN: '',
  LOWER_LIMIT: 'down1',
  FALL: 'down',
};

enum Markets {
  'KOSPI' = '코스피',
  'KOSDAQ' = '코스닥',
  'NYS' = '뉴욕거래소',
  'AMX' = '아멕스',
  'NAS' = '나스닥',
  'NAI' = '나스닥 지수',
  'DJI' = '다우존스 지수',
  'SPI' = 'S&P 지수',
}

enum RegularHourStatus {
  'BEFORE',
  'CONTINUOUS',
  'CLOSING',
}
//
// const regularHourStatusText = {
//   BEFORE: '개장전',
//   CONTINUOUS: '장중',
//   CLOSING: '장종료',
// };

interface FundamentalSummary {
  koreanName: string;
  shortCode: string;
  market: keyof typeof Markets;
  fundamentalScore?: number;
  fundamentalScoreType?: string;
  fundamentalDiagnosisDate?: string;
  titleTag?: keyof typeof ScoreTypes;
  tradePrice: number;
  tradeTime: string;
  change: Change;
  changePrice: number;
  signedChangeRate: number;
  regularHoursStatus: keyof typeof RegularHourStatus;
  date: string;
}

interface HotNews {
  id: string;
  title: string;
  displayTitle?: string;
  url: string;
  type: NewsType;
  publisher: string;
  publishedDatetime: string;
  shortenCodes: string[];
  section: string;
  hasDetail?: boolean;
  categoryKey?: string;
  disclosureType?: DisclosureType;
}

const NEWS_TYPE_1001 = 1_001;

const numberFormatter = new Intl.NumberFormat('ko-KR');
const percentFormatter = new Intl.NumberFormat('ko-KR', { style: 'percent', minimumFractionDigits: 2 });
const RelatedStockItem = memo(function RelatedStockItem({ data, os }: { data: FundamentalSummary; os?: BannerOs }) {
  const {
    koreanName,
    shortCode,
    market,
    fundamentalScore,
    fundamentalScoreType,
    titleTag,
    tradePrice,
    change,
    changePrice,
    signedChangeRate,
  } = data;
  const newsId = useContext(NewsIdContext);

  const [hotNews, setHotNews] = useState<HotNews[]>();
  useEffect(() => {
    if (shortCode) {
      request({
        method: 'GET',
        url: buildNewsHot(shortCode),
        params: {
          exceptNewsId: newsId,
        },
      })
        .then((r) => {
          setHotNews(r?.data?.data?.news);
        })
        .catch((error) => {
          let customError: unknown;

          const classifiedError = classifyHttpError(error);
          if (isCustomError(classifiedError)) {
            customError = classifiedError;
          } else {
            customError = new UnknownError(error);
          }

          captureException(customError);
        });
    }
  }, [shortCode, newsId]);

  const isDelayedMarket = useMemo(() => includes(delayedMarketList, market), [market]);
  const enableScheme = !!os;

  const getHotNewsUrl = (news: HotNews) => {
    if (uaHelper.isStockplusApp()) {
      return news.hasDetail
        ? `stockplus://disclosure/${news.id}`
        : `stockplus://newsDetail?id=${news.id}&section=${news.section}`;
    } else {
      return news?.type === NEWS_TYPE_1001 ? `${newsWebHost}/m?${stringify({ news_id: news.id, os })}` : news.url;
    }
  };

  const isUaIos = useMemo(() => {
    return parser.getOS()?.name === 'iOS';
  }, []);

  const handleStockNameClick = () => {
    trackEvent('종목_클릭', {
      page: '뉴스상세',
      eventDetail_1: shortCode,
      pageDetail: '뉴스에언급된종목',
    });
  };

  const handleNewsClick = (news: HotNews) => {
    if (news.type === NewsType.DISCLOSURE) {
      trackEvent('공시_클릭', {
        page: '뉴스상세',
        pageDetail: '뉴스에언급된종목',
        eventLabel_1: news.categoryKey,
        eventLabel_2: AMPLITUDE_DISCLOSURE_TYPE[news.disclosureType],
        eventDetail_1: news.id,
        eventDetail_2: shortCode,
      });
    } else {
      trackEvent('뉴스_클릭', {
        page: '뉴스상세',
        pageDetail: '뉴스에언급된종목',
        eventDetail_1: news.id,
        eventDetail_2: shortCode,
      });
    }
  };

  return (
    <>
      <div className="stockB">
        <a
          href={
            enableScheme
              ? buildViewAssetScheme(shortCode)
              : isUaIos
              ? buildViewStockOneLink(shortCode)
              : buildViewAssetOneLink(shortCode)
          }
          onClick={handleStockNameClick}>
          <div className="titComp">
            <div
              className="valueB"
              onClick={function (e) {
                e.preventDefault();
                window.location.href = enableScheme
                  ? buildFundamentalScheme(shortCode)
                  : isUaIos
                  ? buildFundamentalOneLinkForIos(shortCode)
                  : buildFundamentalOneLink(shortCode);
              }}>
              {fundamentalScoreType ? (
                <>
                  <span className="chart">
                    <FundamentalScoreMark score={fundamentalScore} scoreType={fundamentalScoreType} />
                  </span>
                  <span className="txt">{titleTag}</span>
                </>
              ) : null}
            </div>
            <span className="tit">
              {isDelayedMarket && <em className="delay">15분 지연</em>}
              {koreanName}
              <em>{shortCode}</em>
              <em className="txt">{Markets[market]}</em>
            </span>
            <div className={`infoCur ${changeClass[change]}`}>
              <strong>{numberFormatter.format(tradePrice)}</strong>
              <p>
                <i>-</i>
                {numberFormatter.format(changePrice)}
              </p>
              <em>{percentFormatter.format(signedChangeRate)}</em>
            </div>
          </div>
        </a>
      </div>
      <div className="listB">
        {hotNews && hotNews.length ? (
          <ul>
            {map(hotNews, (news) => {
              return (
                <li key={news.id}>
                  <a href={getHotNewsUrl(news)} onClick={() => handleNewsClick(news)}>
                    {news.displayTitle || news.title}
                  </a>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="txt">종목의 뉴스가 없습니다.</p>
        )}
      </div>
    </>
  );
});

export const RelatedStock = memo(function RelatedStock({
  shortenCodes,
  os,
}: {
  shortenCodes: string[];
  os?: BannerOs;
}) {
  const [summaries, setSummaries] = useState<{ [key: string]: FundamentalSummary }>();
  useEffect(() => {
    if (shortenCodes && shortenCodes.length > 0) {
      request({
        method: 'GET',
        url: buildQuoteSummary(shortenCodes),
      })
        .then((r) => {
          setSummaries(keyBy(r?.data?.data, 'shortCode'));
        })
        .catch((error) => {
          let customError: unknown;

          const classifiedError = classifyHttpError(error);
          if (isCustomError(classifiedError)) {
            customError = classifiedError;
          } else {
            customError = new UnknownError(error);
          }

          captureException(customError);
        });
    }
  }, [shortenCodes]);

  return !isEmpty(summaries) ? (
    <article className="articleB mt10 end">
      <div className="titB">
        <h3>뉴스에 언급된 종목</h3>
        <em>{formatCurrentTime()} 기준</em>
      </div>
      <div className="stockNews">
        {map(summaries, (summary: FundamentalSummary) => {
          return <RelatedStockItem data={summary} key={summary.shortCode} os={os} />;
        })}
      </div>
    </article>
  ) : null;
});
